import { createMemo, createSignal, createResource, For, Show, JSXElement } from "solid-js";

import { getFolderContents } from "~/_server/s3";

import { S3Folders } from "./s3/S3Folders";
import { S3Files } from "./s3/S3Files";
import { S3FilesType } from "~/lib/types";

export const LogBook = ( props: { userId: string, skipShared: boolean } ) => {
	const [sidebarOpen, setSidebarOpen] = createSignal( false );

	let sections = createMemo( () => {
		const baseSections = [
			{ name: "1 - Scheme Documents", prefix: "logbook/shared/scheme-documents/" },
			{ name: "2 - Quality Assurance", prefix: `logbook/${props.userId}/quality-assurance/` },
			{ name: "3A - Regulatory Documents", prefix: `logbook/shared/regulatory-documents/` },
			{ name: "3B - Technical Standards and Guidance", prefix: `logbook/shared/technical-standards/` },
			{ name: "3C - SAP Methodology and Conventions", prefix: `logbook/shared/sap/` },
			{ name: "3D - Additional Standards & Documents", prefix: `logbook/${props.userId}/additional-standards/` },
			{ name: "4 - PI Insurance Certificates", prefix: `logbook/${props.userId}/pi/` },
			{ name: "5 - CPD Logbooks", prefix: `logbook/${props.userId}/logbooks/` },
			{ name: "6 - Correspondance with Scheme", prefix: `logbook/${props.userId}/correspondance/` },
			{ name: "7 - Previous Audit Reports", prefix: `logbook/${props.userId}/audit-reports/` },
			{ name: "8 - Certificates Issued", prefix: `logbook/${props.userId}/certificates-issued/` },
			{ name: "9 - Scheme Code of Conduct - Signed and Scanned", prefix: `logbook/${props.userId}/code-of-conduct/` },
			{ name: "10 - Membership Certificates", prefix: `logbook/${props.userId}/membership-certificates/` },
			{ name: "11 - SBEM", prefix: `logbook/${props.userId}/sbem/` },
		];

		return props.skipShared ? baseSections.filter( section => !section.prefix.includes( "shared" ) ) : baseSections;
	} );

	const [prefix, setPrefix] = createSignal( sections()[0].prefix );

	const currentSection = createMemo( () => sections().find( section => prefix().startsWith( section.prefix ) ) );

	const [objects, { refetch: refetchS3Path }] = createResource<S3FilesType | undefined>( prefix, ( path: string ) => getFolderContents( path ) );

	const goUp = () => {
		const parts = prefix().split( "/" ).filter( Boolean );
		parts.pop();
		setPrefix( parts.length ? parts.join( "/" ) + "/" : "" );
	};

	return (
		<>
			{/* mobile top bar */}
			<div class="lg:hidden flex items-center justify-between p-4 border-b">
				<h2 class="text-lg font-semibold">Log Book</h2>
				<button
					type="button"
					class="p-2 border rounded"
					onClick={() => setSidebarOpen( !sidebarOpen() )}
				>
					☰
				</button>
			</div>

			<div class="relative flex flex-col lg:flex-row text-gray-800 font-sans">
				{/* Sidebar */}
				<aside
					class={`fixed inset-y-0 left-0 z-40 bg-white lg:bg-transparent shadow-lg transform transition-transform
				  ${sidebarOpen() ? "translate-x-0" : "-translate-x-full"}
				  lg:static lg:translate-x-0 lg:w-fit p-6 space-y-6`}
				>
					<h2 class="text-xl font-semibold">Sections</h2>
					<nav class="space-y-2 text-sm">
						<For each={sections()}>
							{( section ) => (
								<p
									class="p-2 cursor-pointer hover:bg-accent hover:text-white truncate"
									classList={{
										"bg-accent": prefix()?.startsWith( section.prefix ),
										"font-bold": prefix()?.startsWith( section.prefix ),
									}}
									onClick={() => {
										setPrefix( section.prefix );
										setSidebarOpen( false ); // close on mobile
									}}
								>
									{section.name}
								</p>
							)}
						</For>
					</nav>

					<Show when={!props.skipShared}>
						<h3 class="font-bold m-0">About Your Logbook</h3>

						<p class="w-72 text-sm">Your logbook is both a resource of scheme information <em>and</em> the place to store documents that support your work as a Certifier of Desgin.</p>
					</Show>
				</aside>

				{/* Main content */}
				<main class="flex-1 p-4 lg:p-12 overflow-y-auto">
					<h2 class="mb-4 text-2xl font-semibold">{currentSection()?.name} / {prefix().replace( currentSection()?.prefix, "" ).replace( /\/$/, " / " )}</h2>

					{/* Folders */}
					<section>
						<h3 class="text-lg font-semibold mb-4">Folders</h3>
							<S3Folders prefix={prefix()} s3Objects={objects} goUp={goUp} refetchS3Path={refetchS3Path} />
					</section>

					{/* Files */}
					<section class="mt-6">
						<h3 class="text-lg font-semibold mb-4">Files</h3>
						<S3Files prefix={prefix()} s3Objects={objects} refetchS3Path={refetchS3Path} allowUpload={!prefix().includes( "shared" ) }/>
					</section>

					<Show when={prefix().includes( "shared" ) && !props.skipShared}>
						<img src="/images/icon-info.svg" class="h-8 w-8 absolute bottom-4 right-4" title="Note that you are not able to add files to those folders that are shared by all certifiers." />
					</Show>
				</main>
			</div>
		</>
	);
};
